import { useEffect, useMemo } from 'react';
import { useConfigStore } from '@/store/useConfigStore';
import { calculateFullCut } from '@/engine/curtainCalculator';
import type { WindowConfig, CurtainStyleConfig, FabricConfig, LiningConfig, PanelCut } from '@/types/curtain';

export function Step4Preview() {
  const { windowConfig, styleConfig, fabricConfig, liningConfig, hardwareConfig, calculation, setCalculation } = useConfigStore();

  const calculatedResult = useMemo(() => {
    try {
      const wc: WindowConfig = {
        roomName: windowConfig.roomName || '',
        windowName: windowConfig.windowName || '',
        trackWidth: windowConfig.trackWidth || 2000,
        finishedDrop: windowConfig.finishedDrop || 2500,
        dropStyle: (windowConfig.dropStyle as any) || 'floating',
        mountType: (windowConfig.mountType as any) || 'wall',
        floorClearance: windowConfig.floorClearance || 15,
      };
      const sc: CurtainStyleConfig = {
        headingStyle: (styleConfig.headingStyle as any) || 's-fold',
        fullnessRatio: styleConfig.fullnessRatio || 0,
        stackBack: styleConfig.stackBack || 200,
        returnWidth: styleConfig.returnWidth || 80,
        overlap: styleConfig.overlap || 50,
        isPair: styleConfig.isPair ?? true,
      };
      const fc: FabricConfig = {
        fabricType: (fabricConfig.fabricType as any) || 'linen',
        fabricCode: fabricConfig.fabricCode || 'CUSTOM',
        fabricWidth: fabricConfig.fabricWidth || 1400,
        verticalRepeat: fabricConfig.verticalRepeat || 0,
        horizontalRepeat: fabricConfig.horizontalRepeat || 0,
        opacity: (fabricConfig.opacity as any) || 'light-filtering',
        pattern: (fabricConfig.pattern as any) || 'solid',
        pricePerMeter: fabricConfig.pricePerMeter || 0,
      };
      const lc: LiningConfig = {
        liningType: (liningConfig.liningType as any) || 'standard',
        liningWidth: liningConfig.liningWidth || 1400,
        pricePerMeter: liningConfig.pricePerMeter || 10,
      };

      return calculateFullCut(wc, sc, fc, lc);
    } catch (e) {
      console.error("Failed to calculate", e);
      return null;
    }
  }, [windowConfig, styleConfig, fabricConfig, liningConfig]);

  useEffect(() => {
    if (calculatedResult) {
      setCalculation(calculatedResult);
    }
  }, [calculatedResult, setCalculation]);

  const displayResult = calculatedResult || calculation;

  if (!displayResult) {
    return (
      <div className="p-8 text-center bg-red-50 rounded-xl border border-red-100">
        <h3 className="text-red-800 font-bold mb-2">Incomplete Specifications</h3>
        <p className="text-red-600 text-sm">Missing required fields to perform calculations. Please go back and verify Track Width, Drop, and Fabric configurations.</p>
      </div>
    );
  }

  // Derive costs from displayResult
  const fabricCost = displayResult.totalFabricMeters * (fabricConfig.pricePerMeter || 0);
  const liningCost = displayResult.liningMeters * (liningConfig.pricePerMeter || 10);
  const hwCost = (hardwareConfig.pricePerMeter || 0) + (hardwareConfig.accessoriesPrice || 0);
  const totalCost = fabricCost + liningCost + hwCost;

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-300">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 mb-2">Calculation Preview</h2>
          <p className="text-slate-600">Review the required materials and costs.</p>
        </div>
        <button
          onClick={() => useConfigStore.getState().setVisualizerOpen(true)}
          className="flex items-center gap-2 px-4 py-2 bg-indigo-50 text-indigo-700 hover:bg-indigo-100 rounded-lg font-medium transition-colors border border-indigo-200"
        >
          <span>👓 View in 3D</span>
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Measurements Summary */}
        <div className="bg-slate-50 p-5 rounded-xl border border-slate-200">
          <h3 className="font-bold text-slate-900 mb-4 border-b border-slate-200 pb-2">Measurements Summary</h3>
          <div className="space-y-3 text-sm">
            <div className="flex justify-between">
              <span className="text-slate-600">Track Width:</span>
              <span className="font-semibold text-slate-900">{windowConfig.trackWidth || 2000} mm</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-600">Finished Drop:</span>
              <span className="font-semibold text-slate-900">{windowConfig.finishedDrop || 2500} mm</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-600">Total Fabric Width:</span>
              <span className="font-semibold text-slate-900">{displayResult.totalFabricWidth} mm</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-600">Number of Widths:</span>
              <span className="font-semibold text-slate-900">{displayResult.numberOfWidths}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-600">Cut Drop per Width:</span>
              <span className="font-semibold text-slate-900">{displayResult.cutDrop} mm</span>
            </div>
          </div>
        </div>

        {/* Requirements */}
        <div className="bg-amber-50 p-5 rounded-xl border border-amber-200">
          <h3 className="font-bold text-amber-900 mb-4 border-b border-amber-200 pb-2">Material Requirements</h3>
          <div className="space-y-3 text-sm">
            <div className="flex justify-between">
              <span className="text-amber-700">Total Fabric:</span>
              <span className="font-bold text-amber-900">{displayResult.totalFabricMeters.toFixed(2)} m</span>
            </div>
            <div className="flex justify-between">
              <span className="text-amber-700">Total Lining:</span>
              <span className="font-bold text-amber-900">{displayResult.liningMeters.toFixed(2)} m</span>
            </div>
            <div className="flex justify-between">
              <span className="text-amber-700">Wastage:</span>
              <span className="font-medium text-amber-900">{displayResult.wastagePercent.toFixed(1)}%</span>
            </div>
            <div className="flex justify-between">
              <span className="text-amber-700">Pattern Adjustment:</span>
              <span className="font-medium text-amber-900">{displayResult.patternAdjustment} mm</span>
            </div>
          </div>
        </div>

        {/* Panel Breakdown */}
        <div className="md:col-span-2 bg-white p-5 rounded-xl border border-slate-200 overflow-x-auto">
          <h3 className="font-bold text-slate-900 mb-4 border-b border-slate-200 pb-2">Panel Breakdown</h3>
          <table className="w-full text-sm text-left">
            <thead className="text-xs text-slate-500 uppercase bg-slate-50">
              <tr>
                <th className="px-4 py-3 rounded-l-lg">Panel</th>
                <th className="px-4 py-3">Cut Drop</th>
                <th className="px-4 py-3">Finished Width</th>
                <th className="px-4 py-3">Seam Allowance</th>
                <th className="px-4 py-3 rounded-r-lg">Note</th>
              </tr>
            </thead>
            <tbody>
              {displayResult.panelBreakdown.map((panel: PanelCut) => (
                <tr key={panel.panelNumber} className="border-b border-slate-100 last:border-0">
                  <td className="px-4 py-3 font-medium text-slate-900">Panel {panel.panelNumber}</td>
                  <td className="px-4 py-3">{panel.cutDrop} mm</td>
                  <td className="px-4 py-3">{panel.finishedWidth} mm</td>
                  <td className="px-4 py-3">{panel.seamAllowance} mm</td>
                  <td className="px-4 py-3 text-slate-500">{panel.isHalfWidth ? "Half-width panel" : "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Cost Estimate */}
        <div className="md:col-span-2 bg-slate-900 text-white p-6 rounded-xl shadow-lg">
          <h3 className="font-bold text-lg mb-4 border-b border-slate-700 pb-3">Cost Estimate</h3>
          <div className="space-y-3 text-sm mb-6">
            <div className="flex justify-between text-slate-300">
              <span>Fabric Cost:</span>
              <span>${fabricCost.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-slate-300">
              <span>Lining Cost:</span>
              <span>${liningCost.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-slate-300">
              <span>Hardware Cost:</span>
              <span>${hwCost.toFixed(2)}</span>
            </div>
          </div>
          <div className="flex justify-between items-end border-t border-slate-700 pt-4">
            <span className="text-lg text-slate-300">Total Estimate</span>
            <span className="text-3xl font-bold text-amber-500">${totalCost.toFixed(2)}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
